/// <reference types="resize-observer-browser" />

import { RefObject, useEffect, useState } from 'react';
import { asyncScheduler, fromEvent, merge, Observable } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

const PIXELS_FROM_BOTTOM_THRESHOLD = 100;
const THROTTLE_TIME_MS = 300;

enum FetchState {
  Ready,
  Loading,
  Cancelled,
}

export type FetchMoreCallback = (isCancelled: () => boolean) => Promise<unknown>;

// Important to define it outside `useInfiniteScroll`, so its reference is memoized
const DEFAULT_NULL_REF = { current: null };

export function useInfiniteScroll({
  containerRef,
  contentRef = DEFAULT_NULL_REF,
  onFetchMore,
  canFetchMore,
}: {
  containerRef: RefObject<HTMLElement> | null;
  contentRef?: RefObject<HTMLElement> | null;
  onFetchMore: FetchMoreCallback;
  canFetchMore: boolean;
}): boolean {
  const [loading, setLoading] = useState<{ callback: FetchMoreCallback | null }>({ callback: null });

  useEffect(() => {
    if (!canFetchMore || !containerRef?.current) {
      return undefined;
    }

    let fetchState: FetchState = FetchState.Ready;

    const setFetchState = (newFetchState: FetchState): void => {
      fetchState = newFetchState;
      if (fetchState === FetchState.Loading) {
        setLoading({ callback: onFetchMore });
      } else {
        setLoading({ callback: null });
      }
    };

    const isCancelled = (): boolean => {
      return fetchState === FetchState.Cancelled;
    };

    const checkScrolledToBottom = (): void => {
      const viewVisibleHeight = containerRef.current?.clientHeight || 0;
      const bottomPosition = (containerRef.current?.scrollTop || 0) + viewVisibleHeight;
      const pixelsFromBottom = (containerRef.current?.scrollHeight || 0) - bottomPosition;
      if (pixelsFromBottom < PIXELS_FROM_BOTTOM_THRESHOLD && fetchState === FetchState.Ready) {
        setFetchState(FetchState.Loading);
        onFetchMore(isCancelled).finally(() => {
          if (!isCancelled()) {
            setFetchState(FetchState.Ready);
          }
        });
      }
    };

    const subscription = merge(
      fromResizeEvent(containerRef.current),
      fromResizeEvent(contentRef?.current),
      fromEvent(containerRef.current, 'scroll')
    )
      .pipe(throttleTime(THROTTLE_TIME_MS, asyncScheduler, { trailing: true }))
      .subscribe(checkScrolledToBottom);

    // Check once for current position
    checkScrolledToBottom();

    return (): void => {
      setFetchState(FetchState.Cancelled);
      subscription.unsubscribe();
    };
  }, [canFetchMore, containerRef, contentRef, onFetchMore]);

  return onFetchMore === loading.callback;
}

function fromResizeEvent(element: Element | null | undefined): Observable<unknown> {
  return new Observable((observer) => {
    const resizeObserver = new ResizeObserver(() => {
      observer.next();
    });

    if (element != null) {
      resizeObserver.observe(element);
    }

    return (): void => {
      resizeObserver.disconnect();
    };
  });
}
